import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { Syringe, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import clsx from 'clsx'
import { getHealthInterventions } from '../../services/api'
import PeriodToggle from './PeriodToggle'

// Owner-view plan, Session 2.2 — treatments + vaccinations grouped by
// product name, with trend vs the preceding equal-length window.

const PERIOD_OPTIONS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
]

const BAR_COLORS = {
  treatment: '#8b5cf6',
  vaccination: '#3b82f6',
}

function TrendPill({ trend }) {
  if (trend === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
        <Minus className="h-3 w-3" />
        flat
      </span>
    )
  }
  const up = trend > 0
  const Icon = up ? TrendingUp : TrendingDown
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 text-[11px]',
      up ? 'text-amber-700' : 'text-green-700'
    )}>
      <Icon className="h-3 w-3" />
      {up ? '+' : ''}{trend}
    </span>
  )
}

export default function HealthInterventionsCard({ ranchId }) {
  const storageKey = `dashboard.healthInterventions.period.${ranchId}`
  const [period, setPeriod] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      return ['week', 'month', 'quarter'].includes(stored) ? stored : 'month'
    } catch { return 'month' }
  })

  const setAndPersist = (next) => {
    setPeriod(next)
    try { localStorage.setItem(storageKey, next) } catch { /* localStorage may be disabled (e.g. private mode) */ }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['healthInterventions', ranchId, period],
    queryFn: ({ signal }) => getHealthInterventions(ranchId, { period, signal }),
    staleTime: 60_000,
  })

  const categories = data?.categories || []
  const chartData = categories.slice(0, 8).map((c) => ({
    ...c,
    // Recharts needs a short visible label on the axis.
    short: c.category.length > 18 ? `${c.category.slice(0, 16)}…` : c.category,
  }))

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Syringe className="h-5 w-5 text-purple-600" />
          Health Interventions
        </h3>
        <PeriodToggle
          ariaLabel="Health interventions period"
          options={PERIOD_OPTIONS}
          value={period}
          onChange={setAndPersist}
        />
      </div>

      {isLoading || !data ? (
        <div className="h-56 animate-pulse bg-gray-50 rounded" />
      ) : categories.length === 0 ? (
        <div className="h-56 flex flex-col items-center justify-center text-gray-500 text-sm">
          <Syringe className="h-10 w-10 mb-2 opacity-30" />
          <p>No treatments or vaccinations logged this {period}.</p>
        </div>
      ) : (
        <>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="short"
                  width={110}
                  tick={{ fontSize: 11, fill: '#374151' }}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null
                    const p = payload[0].payload
                    return (
                      <div className="bg-white p-2.5 shadow-lg rounded border border-gray-200 text-xs">
                        <p className="font-medium text-gray-900">{p.category}</p>
                        <p className="text-gray-600 capitalize">{p.event_type}</p>
                        <p className="mt-1 text-gray-900">
                          {p.count} this {period} · prev: {p.previous_count}
                        </p>
                      </div>
                    )
                  }}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={BAR_COLORS[entry.event_type] || '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-3 space-y-1.5 max-h-40 overflow-y-auto">
            {categories.map((c) => (
              <li
                key={`${c.event_type}-${c.category}`}
                className="flex items-center justify-between text-xs"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                    style={{ background: BAR_COLORS[c.event_type] || '#94a3b8' }}
                  />
                  <span className="text-gray-700 truncate">{c.category}</span>
                </span>
                <span className="flex items-center gap-3 flex-shrink-0 ml-2">
                  <span className="text-gray-900 font-medium">{c.count}</span>
                  <TrendPill trend={c.trend} />
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
